import React, { useEffect, useState } from "react";
import GlobalVariable from "../../GlobalVariable";
import api from "../../api";

interface Question {
    id: number;
    text: string;
    type: string;
    answers: { id: number; text: string }[];
}

interface LessonTestProps {
    lessonId: number;
    questions: Question[];
}

interface TestAttemptAnswer {
    questionId: number;
    answerId?: number;
    textAnswer?: string;
}

type TestResult = {
    correct: number;
    total: number;
    percent?: number;
};

const LessonTest: React.FC<LessonTestProps> = ({ lessonId, questions }) => {
    const [attemptId, setAttemptId] = useState<number | null>(null);
    const [answers, setAnswers] = useState<Record<number, string | number>>({});
    const [step, setStep] = useState(0);
    const [finished, setFinished] = useState(false);
    const [result, setResult] = useState<TestResult | null>(null);
    const [error, setError] = useState("");
    const [isStarting, setIsStarting] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        let isActive = true;

        setAttemptId(null);
        setAnswers({});
        setStep(0);
        setFinished(false);
        setResult(null);
        setError("");
        setIsStarting(true);

        api.post(
            GlobalVariable.staffBack() + "/api/test/attempt/start",
            { lessonId },
            { withCredentials: true }
        )
            .then((res) => {
                if (!isActive) return;

                setAttemptId(res.data.attemptId);
                const initAnswers: Record<number, string | number> = {};
                (res.data.answers || []).forEach((answer: TestAttemptAnswer) => {
                    if (answer.answerId !== undefined && answer.answerId !== null) {
                        initAnswers[answer.questionId] = answer.answerId;
                    } else if (answer.textAnswer) {
                        initAnswers[answer.questionId] = answer.textAnswer;
                    }
                });
                setAnswers(initAnswers);
            })
            .catch((requestError) => {
                if (!isActive) return;

                const serverMessage = requestError?.response?.data?.error;
                setError(serverMessage || "Не удалось загрузить тест.");
            })
            .finally(() => {
                if (isActive) {
                    setIsStarting(false);
                }
            });

        return () => {
            isActive = false;
        };
    }, [lessonId]);

    if (!questions || questions.length === 0) return null;
    if (isStarting || attemptId === null) return <div>{error || "Загрузка теста..."}</div>;

    const q = questions[step];

    const handleAnswer = (value: number | string) => {
        setError("");
        setAnswers((prev) => ({ ...prev, [q.id]: value }));

        api.post(
            GlobalVariable.staffBack() + "/api/test/attempt/answer",
            {
                attemptId,
                questionId: q.id,
                answerId: q.type === "choice" ? value : undefined,
                textAnswer: q.type === "text" ? value : undefined,
            },
            { withCredentials: true }
        ).catch((requestError) => {
            const serverMessage = requestError?.response?.data?.error;
            setError(serverMessage || "Не удалось сохранить ответ.");
        });
    };

    const next = () => setStep((prev) => prev + 1);
    const prev = () => setStep((prev) => prev - 1);

    const finish = () => {
        setIsSubmitting(true);
        setError("");

        api.post(
            GlobalVariable.staffBack() + "/api/test/attempt/finish",
            { attemptId },
            { withCredentials: true }
        )
            .then((res) => {
                setFinished(true);
                setResult(res.data);
            })
            .catch((requestError) => {
                const serverMessage = requestError?.response?.data?.error;
                setError(serverMessage || "Не удалось завершить тест.");
            })
            .finally(() => setIsSubmitting(false));
    };

    const repeatTest = () => {
        window.location.reload();
    };

    if (finished && result) {
        if (result.percent !== undefined && result.percent >= 80) {
            return (
                <div className="chio-card mb-3 p-4 text-center">
                    <h4 style={{color: "#24b47e", fontWeight: 700}}>Тест пройден успешно</h4>
                    <div className="mt-3 mb-2" style={{fontSize: "1.12rem"}}>
                        Правильных ответов: <b>{result.correct} из {result.total}</b>
                    </div>
                    <div style={{fontSize: "1rem", color: "#2563eb", fontWeight: 600}}>
                        ЧиоКоины начисляются автоматически после успешной попытки.
                    </div>
                </div>
            );
        }

        return (
            <div className="chio-card mb-3 p-4 text-center">
                <div style={{fontSize: "1.16rem", fontWeight: 600, color: "#e72f44"}}>
                    Правильных ответов {result.correct} из {result.total}.<br/>Пройди тест заново.
                </div>
                <button
                    className="btn btn-primary mt-4"
                    style={{padding: "0.65rem 2.1rem", fontWeight: 600, fontSize: "1.1rem"}}
                    onClick={repeatTest}
                >
                    Пройти тест
                </button>
            </div>
        );
    }

    return (
        <div className="chio-card mb-3 p-3">
            <div>
                <div>
                    <b>Вопрос {step + 1} из {questions.length}</b>
                </div>
                <div className="mb-2">{q.text}</div>
                {q.type === "choice" ? (
                    q.answers.map((answer) => (
                        <div key={answer.id} className="mb-1">
                            <label>
                                <input
                                    type="radio"
                                    name={`q_${q.id}`}
                                    checked={answers[q.id] === answer.id}
                                    onChange={() => handleAnswer(answer.id)}
                                />
                                {' '}
                                {answer.text}
                            </label>
                        </div>
                    ))
                ) : (
                    <input
                        type="text"
                        className="form-control"
                        value={answers[q.id] || ""}
                        onChange={(e) => handleAnswer(e.target.value)}
                    />
                )}
                {error && <div className="text-danger mt-2">{error}</div>}
                <div className="d-flex justify-content-between mt-3">
                    <button className="btn btn-outline-secondary" onClick={prev} disabled={step === 0 || isSubmitting}>
                        Назад
                    </button>
                    {step < questions.length - 1 ? (
                        <button
                            className="btn btn-primary"
                            onClick={next}
                            disabled={!answers[q.id] || isSubmitting}
                        >
                            Далее
                        </button>
                    ) : (
                        <button className="btn btn-success" onClick={finish} disabled={!answers[q.id] || isSubmitting}>
                            {isSubmitting ? 'Завершение...' : 'Завершить'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LessonTest;
